import { ReactElement } from 'react';

import { AnimatePresence, m as motion } from 'framer-motion';
import { ErrorBoundary } from 'react-error-boundary';

import Spinner from 'chime-ui/components/Spinner';

import { ReceiveFromAnyoneConfirmation } from 'components/SubPages/Confirmation';
import {
  ErrorClaimed,
  ErrorContextualXState,
  ErrorExpired,
  ErrorGeneric,
} from 'components/SubPages/Error';
import MaintenanceMessage from 'components/SubPages/Error/MaintenanceMessage';
import { ReceiveFromAnyoneLanding } from 'components/SubPages/Landing';
import useReceiveFromAnyoneToken, {
  ClaimStatus,
} from 'components/SubPages/Token/Request/useReceiveFromAnyoneToken';

import { MachineContext } from 'hooks/useMachineContext';

import { trackPayAnyoneReceiverError } from 'utils/segment/trackingEvents';

import { ReceiveFromAnyoneClaimInfo } from '../../ClaimInfo';

type ReceiveFromAnyoneTokenProps = {
  data?: any;
  isError: boolean;
};

function ReceiveFromAnyoneToken({
  data,
  isError,
}: ReceiveFromAnyoneTokenProps): ReactElement {
  const {
    isMaintenanceModeEnabled,
    claimStatus,
    navigationMachine,
  } = useReceiveFromAnyoneToken(data);

  if (isMaintenanceModeEnabled) {
    return <MaintenanceMessage />;
  }

  const { state, service } = navigationMachine;

  const ErrorFallback = ({ publicId = data.publicId }) => {
    trackPayAnyoneReceiverError(
      'P2P Receive Landing Page',
      'Something went wrong',
      publicId
    );

    return <ErrorGeneric showLogin={false} />;
  };

  if (isError) {
    return <ErrorFallback publicId={null} />;
  }

  if (claimStatus === ClaimStatus.succeeded) {
    return <ErrorClaimed />;
  }

  if (claimStatus === ClaimStatus.expired) {
    return <ErrorExpired referralToken={data.sender.referralToken} />;
  }

  if (claimStatus === ClaimStatus.pending) {
    const handleError = (error: Error, info) => {
      window.Rollbar?.error(error.message, error, info);
    };

    return (
      <ErrorBoundary
        fallback={<ErrorFallback publicId={data.public} />}
        onError={handleError}
      >
        <MachineContext.Provider value={service}>
          {state.matches('landing') ? (
            <PageFadeInAnimation>
              <ReceiveFromAnyoneLanding />
            </PageFadeInAnimation>
          ) : state.matches('claimInfo') ? (
            <PageFadeInAnimation>
              <ReceiveFromAnyoneClaimInfo />
            </PageFadeInAnimation>
          ) : state.matches('confirmation') ? (
            <PageFadeInAnimation>
              <ReceiveFromAnyoneConfirmation />
            </PageFadeInAnimation>
          ) : state.matches('error') ? (
            <PageFadeInAnimation>
              <ErrorContextualXState showCta={false} showLogin={false} />
            </PageFadeInAnimation>
          ) : state.matches('loading') ? (
            <Spinner />
          ) : (
            <PageFadeInAnimation>
              <ErrorGeneric showLogin={false} />
            </PageFadeInAnimation>
          )}
        </MachineContext.Provider>
      </ErrorBoundary>
    );
  }

  return <ErrorFallback />;
}

const PageFadeInAnimation = ({ children }) => (
  <AnimatePresence>
    <motion.div
      key={'page fade in'}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: 'easeIn' }}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

export default ReceiveFromAnyoneToken;
